/**
 * Region Tool — Annotate > Detail > Region split button.
 *
 * Two variants behind one sketch engine:
 *   filled  — hatched closed area (pattern chosen from toolProperties)
 *   masking — blank mask that hides model geometry behind it
 *
 * Shared boundary sketch: click points, close near the start or press Enter.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import i18n from '../i18n';

export type RegionVariant = 'filled' | 'masking';

export interface RegionGeometryData {
    boundary: [number, number][];
    elevation: number;
    variant: RegionVariant;
    pattern: string;
}

export interface RegionElement extends Element {
    category: 'Detail Items';
    geometryData: RegionGeometryData;
}

/** Filled-region hatch patterns available in the options bar (BIM list). */
export const REGION_PATTERNS = [
    'Diagonal Crosshatch',
    'ANSI31',
    'ANSI37',
    'Concrete',
    'Insulation',
    'Solid Fill',
] as const;

export class RegionTool extends BaseTool {
    id = 'region';
    name: string;
    cursor = 'crosshair';

    private variant: RegionVariant;
    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    constructor(variant: RegionVariant = 'filled') {
        super();
        this.variant = variant;
        this.id = variant === 'filled' ? 'filled_region' : 'masking_region';
        this.name = variant === 'filled' ? 'Filled Region' : 'Masking Region';
    }

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.sketchBoundaryHint', { tool: this.name }));
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);

        // Check if closed
        if (this.points.length > 2) {
            const start = this.points[0];
            if (snapped.distanceTo(start) < 0.2) {
                this.points.pop();
                this.finishSketch();
                return;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finishSketch();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: previewPoints,
                closed: false
                // No fixed color: the viewport draws previews with the
                // theme ink (white in dark mode, black in light mode)
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishSketch() {
        if (!this.context) return;

        const boundary: [number, number][] = this.points.map(p => [p.x, p.y]);
        const elevation = this.points[0].z;
        const pattern = this.variant === 'masking'
            ? 'No Mask'
            : (this.context.bimStore.toolProperties.filledRegionPattern ?? 'Diagonal Crosshatch');

        // Shoelace area of the closed loop (m²) for the element readout.
        let area = 0;
        for (let i = 0; i < boundary.length; i++) {
            const [x1, y1] = boundary[i];
            const [x2, y2] = boundary[(i + 1) % boundary.length];
            area += x1 * y2 - x2 * y1;
        }
        area = Math.abs(area) / 2;

        const geometryData: RegionGeometryData = {
            boundary: boundary,
            elevation: elevation,
            variant: this.variant,
            pattern,
        };

        const region: RegionElement = {
            id: uuidv4(),
            category: 'Detail Items',
            type: this.variant === 'masking' ? 'Masking Region' : `Filled Region: ${pattern}`,
            parameters: {
                'Area': `${area.toFixed(2)} m²`,
                'Pattern': pattern,
                'Region Type': this.variant === 'masking' ? 'Masking' : 'Filled',
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(region);

        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
}
